/**
 * Floating header: slide the mobile menu up from below the bar (instead of from the left).
 * Header corner merge is CSS (floating-header.css). Panel top corners need a short WAAPI on open
 * because the drawer starts at display:none — CSS can't transition from a non-rendered state.
 *
 * Keep ENTER_MS / MERGE_* in sync with the CSS custom properties on .header--floating.
 */
(() => {
  const ENTER_MS = 360;
  const LEAVE_MS = 300;
  const OVERLAY_MS = 200;
  const MERGE_DELAY_MS = 320;
  const MERGE_MS = 240;
  const EASE_IN = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const EASE_OUT = 'cubic-bezier(0.32, 0, 0.67, 0)';
  const MERGE_EASE = 'ease';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Theme DialogElement resolves leave animations then sets display:none.
   * A cancelled run must not resolve, or a re-opening drawer gets hidden.
   */
  function wrap(animations) {
    const list = animations.filter(Boolean);
    let cancelled = false;
    const finished = Promise.all(list.map((a) => a.finished.catch(() => {})));
    const settled = new Promise((resolve) => {
      finished.then(() => {
        if (!cancelled) resolve();
      });
    });

    return {
      then: (onFulfilled, onRejected) => settled.then(onFulfilled, onRejected),
      complete() {
        list.forEach((a) => {
          try {
            a.finish();
          } catch (_) {}
        });
      },
      cancel() {
        cancelled = true;
        list.forEach((a) => {
          try {
            a.cancel();
          } catch (_) {}
        });
      },
    };
  }

  function patch() {
    const Drawer = customElements.get('header-menu-drawer');
    if (!Drawer || Drawer.__floatingSlidePatched) return;

    Drawer.__floatingSlidePatched = true;
    const proto = Drawer.prototype;
    const originalShow = proto.show;
    const originalEnter = proto.createEnterAnimationControls;
    const originalLeave = proto.createLeaveAnimationControls;

    /* Clear leftover leave anims before Stretch measures --menu-offset-top. */
    proto.show = function show(...args) {
      if (this.closest('.header--floating')) {
        this.__floatingHeaderControls?.cancel();
        this.__floatingHeaderControls = null;
      }
      return originalShow.apply(this, args);
    };

    proto.createEnterAnimationControls = function createEnterAnimationControls() {
      const header = this.closest('.header--floating');
      if (!header) return originalEnter.call(this);

      const overlay = this.getShadowPartByName('overlay');
      const content = this.getShadowPartByName('content');
      const reduce = prefersReducedMotion();
      const radius = getComputedStyle(header).getPropertyValue('--button-border-radius').trim() || '0px';
      const animations = [];

      if (overlay) {
        animations.push(
          overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: OVERLAY_MS,
            easing: 'ease-in-out',
          })
        );
      }

      if (content) {
        if (reduce) {
          animations.push(content.animate([{ opacity: 0 }, { opacity: 1 }], { duration: OVERLAY_MS }));
        } else {
          animations.push(
            content.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
              duration: ENTER_MS,
              easing: EASE_IN,
            })
          );
          /*
            fill:backwards holds the rounded top during MERGE_DELAY so the panel looks detached
            until it meets the bar — CSS alone can't do this after display:none.
          */
          animations.push(
            content.animate(
              [
                { borderTopLeftRadius: radius, borderTopRightRadius: radius },
                { borderTopLeftRadius: '0px', borderTopRightRadius: '0px' },
              ],
              {
                duration: reduce ? OVERLAY_MS : MERGE_MS,
                delay: reduce ? 0 : MERGE_DELAY_MS,
                easing: MERGE_EASE,
                fill: 'backwards',
              }
            )
          );
        }
      }

      this.__floatingHeaderControls = wrap(animations);
      return this.__floatingHeaderControls;
    };

    proto.createLeaveAnimationControls = function createLeaveAnimationControls() {
      if (!this.closest('.header--floating')) return originalLeave.call(this);

      const overlay = this.getShadowPartByName('overlay');
      const content = this.getShadowPartByName('content');
      const reduce = prefersReducedMotion();
      const animations = [];

      if (overlay) {
        animations.push(
          overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: OVERLAY_MS,
            easing: 'ease-in-out',
            fill: 'forwards',
          })
        );
      }

      if (content) {
        animations.push(
          reduce
            ? content.animate([{ opacity: 1 }, { opacity: 0 }], {
                duration: OVERLAY_MS,
                fill: 'forwards',
              })
            : content.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }], {
                duration: LEAVE_MS,
                easing: EASE_OUT,
                /* Hold until display:none; show() cancels this before Stretch measures offsets. */
                fill: 'forwards',
              })
        );
      }

      this.__floatingHeaderControls = wrap(animations);
      return this.__floatingHeaderControls;
    };
  }

  if (customElements.get('header-menu-drawer')) {
    patch();
  } else {
    customElements.whenDefined('header-menu-drawer').then(patch);
  }
})();
